"use client";

import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/lib/motion";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { cn } from "@/lib/utils";

/* Animated mesh-gradient backdrop — a slow, liquid wash of the brand's signal
   blue, bloomed with the hero's orange, that sits behind a card grid or a
   section.

   Adapted from the Stripe-style "MiniGL" gradient: a subdivided plane whose
   vertices are displaced by simplex noise, with extra colour layers blended in
   by their own noise fields, so the surface reads as several sheets of colour
   drifting through one another. The vertex shader does all the work; the
   fragment shader just paints the interpolated colour.

   Reworked for this codebase:
   · fully typed — no `any`
   · sized from its CONTAINER via ResizeObserver, not `window`, so it can back
     a card instead of only a full-screen hero (the original also leaked a
     window resize listener that was never removed)
   · exactly four colours. The shader gates each wave layer on
     `u_active_colors[i + 1]`, and that uniform is a `vec4` — a fifth colour
     indexes out of bounds, which is undefined behaviour in GLSL
   · vertical noise amplitude scales with height, so a 400px card gets the
     same proportional motion a 900px hero does
   · theme-aware defaults via usePrefersLight, alpha-blended so it tints the
     surface underneath rather than painting over it
   · parks itself when scrolled out of view, and holds a single still frame
     under prefers-reduced-motion

   Decorative only — always rendered aria-hidden behind content. */

/** Base colour plus three wave layers. More than four breaks the shader. */
export type GradientWaveColors = [string, string, string, string];

type UniformScope = "vertex" | "fragment";
type UniformType = "float" | "int" | "vec2" | "vec3" | "vec4" | "mat4" | "array" | "struct";
type UniformValue = number | number[] | Uniform[] | Record<string, Uniform>;

interface UniformOptions {
  value: UniformValue;
  type?: UniformType;
  /** Skip emitting this declaration in one of the two shader stages. */
  excludeFrom?: UniformScope;
}

class Uniform {
  readonly type: UniformType;
  value: UniformValue;
  readonly excludeFrom?: UniformScope;

  constructor({ value, type = "float", excludeFrom }: UniformOptions) {
    this.value = value;
    this.type = type;
    this.excludeFrom = excludeFrom;
  }

  update(gl: WebGLRenderingContext, location: WebGLUniformLocation | null): void {
    if (location === null) return;
    switch (this.type) {
      case "float":
        gl.uniform1f(location, this.value as number);
        break;
      case "int":
        gl.uniform1i(location, this.value as number);
        break;
      case "vec2":
        gl.uniform2fv(location, this.value as number[]);
        break;
      case "vec3":
        gl.uniform3fv(location, this.value as number[]);
        break;
      case "vec4":
        gl.uniform4fv(location, this.value as number[]);
        break;
      case "mat4":
        gl.uniformMatrix4fv(location, false, this.value as number[]);
        break;
      default:
        break; // array / struct are flattened before they reach a location
    }
  }

  /** GLSL declaration for this uniform, recursing through structs and arrays. */
  getDeclaration(name: string, scope: UniformScope, length?: number): string {
    if (this.excludeFrom === scope) return "";
    const suffix = length ? `[${length}]` : "";

    if (this.type === "array") {
      const members = this.value as Uniform[];
      return `${members[0].getDeclaration(name, scope, members.length)}\nconst int ${name}_length = ${members.length};`;
    }

    if (this.type === "struct") {
      const bare = name.replace("u_", "");
      const structName = bare.charAt(0).toUpperCase() + bare.slice(1);
      const fields = Object.entries(this.value as Record<string, Uniform>)
        .map(([field, uniform]) => uniform.getDeclaration(field, scope).replace(/^uniform/, ""))
        .join("");
      return `uniform struct ${structName} {\n${fields}\n} ${name}${suffix};`;
    }

    return `uniform ${this.type} ${name}${suffix};`;
  }
}

type UniformMap = Record<string, Uniform>;

/** A uniform flattened to the single GLSL location it actually binds to. */
interface BoundUniform {
  uniform: Uniform;
  location: WebGLUniformLocation | null;
}

interface AttributeOptions {
  target: number;
  size: number;
  type?: number;
}

class Attribute {
  readonly target: number;
  readonly size: number;
  readonly type: number;
  readonly buffer: WebGLBuffer;
  values?: Float32Array | Uint16Array;

  constructor(gl: WebGLRenderingContext, { target, size, type }: AttributeOptions) {
    this.target = target;
    this.size = size;
    this.type = type ?? gl.FLOAT;
    this.buffer = gl.createBuffer() as WebGLBuffer;
  }

  update(gl: WebGLRenderingContext): void {
    if (!this.values) return;
    gl.bindBuffer(this.target, this.buffer);
    gl.bufferData(this.target, this.values, gl.STATIC_DRAW);
  }

  attach(gl: WebGLRenderingContext, name: string, program: WebGLProgram): number {
    const location = gl.getAttribLocation(program, name);
    this.use(gl, location);
    return location;
  }

  use(gl: WebGLRenderingContext, location: number): void {
    gl.bindBuffer(this.target, this.buffer);
    if (this.target !== gl.ARRAY_BUFFER || location < 0) return;
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, this.size, this.type, false, 0, 0);
  }
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type) as WebGLShader;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`GradientWave shader failed to compile: ${log}`);
  }
  return shader;
}

function declarations(uniforms: UniformMap, scope: UniformScope): string {
  return Object.entries(uniforms)
    .map(([name, uniform]) => uniform.getDeclaration(name, scope))
    .join("\n");
}

/** #rrggbb → normalised rgb triple. */
function normalizeColor(hex: string): number[] {
  const value = parseInt(hex.replace("#", ""), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

const VERTEX_SHADER = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0)) +
      i.y + vec4(0.0, i1.y, i2.y, 1.0)) +
      i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
  return blend * opacity + base * (1.0 - opacity);
}

varying vec3 v_color;

void main() {
  float time = u_time * u_global.noiseSpeed;
  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;

  float tilt = resolution.y / 2.0 * uvNorm.y;
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline;
  float offset = resolution.x / 2.0 * u_vertDeform.incline
    * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);

  float noise = snoise(vec3(
    noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow,
    noiseCoord.y * u_vertDeform.noiseFreq.y,
    time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed
  )) * u_vertDeform.noiseAmp;

  noise *= 1.0 - pow(abs(uvNorm.y), 2.0);
  noise = max(0.0, noise);

  vec3 pos = vec3(position.x, position.y + tilt + incline + noise - offset, position.z);

  v_color = u_baseColor;

  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.0) {
      WaveLayers layer = u_waveLayers[i];
      float layerNoise = smoothstep(
        layer.noiseFloor,
        layer.noiseCeil,
        snoise(vec3(
          noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow,
          noiseCoord.y * layer.noiseFreq.y,
          time * layer.noiseSpeed + layer.noiseSeed
        )) / 2.0 + 0.5
      );
      v_color = blendNormal(v_color, layer.color, pow(layerNoise, 4.0));
    }
  }

  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`;

const FRAGMENT_SHADER = /* glsl */ `
varying vec3 v_color;

void main() {
  gl_FragColor = vec4(v_color, u_alpha);
}`;

const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/* The shader reads noise at `resolution * uvNorm * noiseFreq`, so the span of
   noise crossing the surface grows with its pixel size — a large tile shows a
   sweeping wave while a small one samples a nearly flat patch of the field and
   reads as a solid block. Holding the span constant instead, and deriving
   noiseFreq from the measured size, gives every tile the same wave regardless
   of how big it is. These two are the original's span at 1440×800. */
const NOISE_SPAN_X = 0.2;
const NOISE_SPAN_Y = 0.23;

interface RendererOptions {
  colors: GradientWaveColors;
  alpha: number;
  /** Vertical displacement as a fraction of the element's height. */
  amplitude: number;
  speed: number;
  /** Offsets the noise field so sibling surfaces don't render the same wave. */
  seed: number;
}

/** Owns the GL objects for one gradient surface. */
class GradientRenderer {
  private readonly gl: WebGLRenderingContext;
  private readonly program: WebGLProgram;
  private readonly uniforms: UniformMap;
  private readonly common: UniformMap;
  private readonly bound: BoundUniform[] = [];
  private readonly attributes: Record<string, Attribute>;
  private readonly attributeLocations: Record<string, number> = {};
  private readonly amplitude: number;

  private xSegments = 1;
  private ySegments = 1;
  private vertexCount = 0;
  private indexCount = 0;
  private width = 1;
  private height = 1;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    { colors, alpha, amplitude, speed, seed }: RendererOptions
  ) {
    const gl = canvas.getContext("webgl", { antialias: true, alpha: true, depth: false });
    if (!gl) throw new Error("WebGL is unavailable");
    this.gl = gl;
    this.amplitude = amplitude;

    const stops = colors.map(normalizeColor);

    this.common = {
      projectionMatrix: new Uniform({ type: "mat4", value: [...IDENTITY] }),
      modelViewMatrix: new Uniform({ type: "mat4", value: [...IDENTITY] }),
      resolution: new Uniform({ type: "vec2", value: [1, 1] }),
      aspectRatio: new Uniform({ type: "float", value: 1 }),
    };

    const waveLayers: Uniform[] = [];
    for (let i = 1; i < stops.length; i++) {
      waveLayers.push(
        new Uniform({
          type: "struct",
          value: {
            color: new Uniform({ type: "vec3", value: stops[i] }),
            noiseFreq: new Uniform({
              type: "vec2",
              value: [2 + i / stops.length, 3 + i / stops.length],
            }),
            noiseSpeed: new Uniform({ value: 11 + 0.3 * i }),
            noiseFlow: new Uniform({ value: 6.5 + 0.3 * i }),
            noiseSeed: new Uniform({ value: 5 + 10 * i + seed * 137 }),
            noiseFloor: new Uniform({ value: 0.1 }),
            noiseCeil: new Uniform({ value: 0.63 + 0.07 * i }),
          },
        })
      );
    }

    this.uniforms = {
      u_time: new Uniform({ value: 0 }),
      u_alpha: new Uniform({ value: alpha, excludeFrom: "vertex" }),
      // one slot per colour: the base plus each wave layer
      u_active_colors: new Uniform({ type: "vec4", value: [1, 1, 1, 1] }),
      u_global: new Uniform({
        type: "struct",
        value: {
          noiseFreq: new Uniform({ type: "vec2", value: [0.00014, 0.00029] }),
          noiseSpeed: new Uniform({ value: 0.000005 * speed }),
        },
      }),
      u_vertDeform: new Uniform({
        type: "struct",
        excludeFrom: "fragment",
        value: {
          incline: new Uniform({ value: 0.4 }),
          offsetTop: new Uniform({ value: -0.4 }),
          offsetBottom: new Uniform({ value: -0.4 }),
          noiseFreq: new Uniform({ type: "vec2", value: [3, 4] }),
          noiseAmp: new Uniform({ value: 0 }), // set from height on resize
          noiseSpeed: new Uniform({ value: 10 }),
          noiseFlow: new Uniform({ value: 3 }),
          noiseSeed: new Uniform({ value: 5 + seed * 137 }),
        },
      }),
      u_baseColor: new Uniform({ type: "vec3", value: stops[0], excludeFrom: "fragment" }),
      u_waveLayers: new Uniform({ type: "array", value: waveLayers, excludeFrom: "fragment" }),
    };

    const prefix = "precision highp float;";
    const vertexSource = [
      prefix,
      "attribute vec4 position;",
      "attribute vec2 uv;",
      "attribute vec2 uvNorm;",
      declarations(this.common, "vertex"),
      declarations(this.uniforms, "vertex"),
      VERTEX_SHADER,
    ].join("\n");
    const fragmentSource = [
      prefix,
      declarations(this.common, "fragment"),
      declarations(this.uniforms, "fragment"),
      FRAGMENT_SHADER,
    ].join("\n");

    this.program = gl.createProgram() as WebGLProgram;
    gl.attachShader(this.program, compileShader(gl, gl.VERTEX_SHADER, vertexSource));
    gl.attachShader(this.program, compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource));
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error(`GradientWave program failed to link: ${gl.getProgramInfoLog(this.program)}`);
    }
    gl.useProgram(this.program);

    this.bind(this.common);
    this.bind(this.uniforms);

    this.attributes = {
      position: new Attribute(gl, { target: gl.ARRAY_BUFFER, size: 3 }),
      uv: new Attribute(gl, { target: gl.ARRAY_BUFFER, size: 2 }),
      uvNorm: new Attribute(gl, { target: gl.ARRAY_BUFFER, size: 2 }),
      index: new Attribute(gl, {
        target: gl.ELEMENT_ARRAY_BUFFER,
        size: 3,
        type: gl.UNSIGNED_SHORT,
      }),
    };
    for (const [name, attribute] of Object.entries(this.attributes)) {
      this.attributeLocations[name] = attribute.attach(gl, name, this.program);
    }

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** Walk structs and arrays down to the leaves that own a GLSL location. */
  private bind(uniforms: UniformMap, prefix?: string): void {
    for (const [key, uniform] of Object.entries(uniforms)) {
      const name = prefix ? `${prefix}.${key}` : key;
      if (uniform.type === "array") {
        (uniform.value as Uniform[]).forEach((member, i) => {
          this.bind({ [`${name}[${i}]`]: member });
        });
      } else if (uniform.type === "struct") {
        this.bind(uniform.value as Record<string, Uniform>, name);
      } else {
        this.bound.push({
          uniform,
          location: this.gl.getUniformLocation(this.program, name),
        });
      }
    }
  }

  /** `width`/`height` are CSS pixels; the drawing buffer is scaled by dpr. */
  resize(width: number, height: number, dpr: number): void {
    const gl = this.gl;
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);

    this.canvas.width = Math.floor(this.width * dpr);
    this.canvas.height = Math.floor(this.height * dpr);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);

    this.common.resolution.value = [this.width, this.height];
    this.common.aspectRatio.value = this.width / this.height;
    // orthographic, in CSS pixels, matching the geometry below
    this.common.projectionMatrix.value = [
      2 / this.width,
      0,
      0,
      0,
      0,
      2 / this.height,
      0,
      0,
      0,
      0,
      -0.001,
      0,
      0,
      0,
      0,
      1,
    ];

    // Displacement is a fraction of the height, so a short card gets the same
    // proportional swell as a tall hero instead of the original's fixed 320px.
    const vertDeform = this.uniforms.u_vertDeform.value as Record<string, Uniform>;
    vertDeform.noiseAmp.value = this.height * this.amplitude;

    // Constant noise span — see NOISE_SPAN_X. Without this a small tile samples
    // a nearly flat patch of the field and shows no wave at all.
    const global = this.uniforms.u_global.value as Record<string, Uniform>;
    global.noiseFreq.value = [NOISE_SPAN_X / this.width, NOISE_SPAN_Y / this.height];

    this.setTopology(Math.ceil(this.width * 0.06), Math.ceil(this.height * 0.06));
    this.setGeometry();
  }

  private setTopology(xSegments: number, ySegments: number): void {
    this.xSegments = Math.max(1, xSegments);
    this.ySegments = Math.max(1, ySegments);
    this.vertexCount = (this.xSegments + 1) * (this.ySegments + 1);

    const uv = new Float32Array(this.vertexCount * 2);
    const uvNorm = new Float32Array(this.vertexCount * 2);
    const index = new Uint16Array(this.xSegments * this.ySegments * 6);

    for (let y = 0; y <= this.ySegments; y++) {
      for (let x = 0; x <= this.xSegments; x++) {
        const i = y * (this.xSegments + 1) + x;
        uv[2 * i] = x / this.xSegments;
        uv[2 * i + 1] = 1 - y / this.ySegments;
        uvNorm[2 * i] = (x / this.xSegments) * 2 - 1;
        uvNorm[2 * i + 1] = 1 - (y / this.ySegments) * 2;

        if (x < this.xSegments && y < this.ySegments) {
          const q = y * this.xSegments + x;
          index[6 * q] = i;
          index[6 * q + 1] = i + 1 + this.xSegments;
          index[6 * q + 2] = i + 1;
          index[6 * q + 3] = i + 1;
          index[6 * q + 4] = i + 1 + this.xSegments;
          index[6 * q + 5] = i + 2 + this.xSegments;
        }
      }
    }

    this.indexCount = index.length;
    this.attributes.uv.values = uv;
    this.attributes.uvNorm.values = uvNorm;
    this.attributes.index.values = index;
    this.attributes.uv.update(this.gl);
    this.attributes.uvNorm.update(this.gl);
    this.attributes.index.update(this.gl);
  }

  private setGeometry(): void {
    const position = new Float32Array(this.vertexCount * 3);
    const segWidth = this.width / this.xSegments;
    const segHeight = this.height / this.ySegments;

    for (let y = 0; y <= this.ySegments; y++) {
      const posY = this.height / -2 + y * segHeight;
      for (let x = 0; x <= this.xSegments; x++) {
        const i = y * (this.xSegments + 1) + x;
        position[3 * i] = this.width / -2 + x * segWidth;
        position[3 * i + 1] = -posY;
        position[3 * i + 2] = 0;
      }
    }

    this.attributes.position.values = position;
    this.attributes.position.update(this.gl);
  }

  render(time: number): void {
    const gl = this.gl;
    this.uniforms.u_time.value = time;

    gl.useProgram(this.program);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    for (const { uniform, location } of this.bound) uniform.update(gl, location);
    for (const [name, attribute] of Object.entries(this.attributes)) {
      attribute.use(gl, this.attributeLocations[name]);
    }
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);
  }

  dispose(): void {
    const gl = this.gl;
    for (const attribute of Object.values(this.attributes)) gl.deleteBuffer(attribute.buffer);
    gl.deleteProgram(this.program);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

/* The brand's signal blue, stepped down to a dark base — three of the four stops
   are blue, so blue leads. Only the last bloom carries the hero's orange, and it
   is heavily desaturated off #ff8a5b: this layer surfaces on top of the others,
   so anything near a saturated orange here reads as a second colour field
   instead of the warm accent it is meant to be.
   Each stop holds the lightness of the ramp it replaced and only rotates hue,
   so the surface keeps the intensity it was tuned at.

   The base stays dark on purpose: a layer only surfaces where its noise clears
   `noiseCeil`, so most of the card holds the base colour and foreground text
   keeps its contrast while the blooms still read as vivid. */
const COLORS_DARK: GradientWaveColors = ["#080d1e", "#1a3a6b", "#365ecf", "#a8613f"];
/** The same family for the light theme, where text is dark instead. */
const COLORS_LIGHT: GradientWaveColors = ["#f1f5ff", "#d8e4ff", "#aac4fb", "#f2cbb4"];

interface Props {
  /** Base colour plus three wave layers. Used for both schemes unless
   *  `colorsLight` is given; defaults follow the colour scheme. */
  colors?: GradientWaveColors;
  /** Light-theme ramp. Canvas colours can't follow CSS, so a caller that wants
   *  its own palette has to supply both halves. */
  colorsLight?: GradientWaveColors;
  /** Opacity of the gradient over whatever sits beneath it. */
  opacity?: number;
  /** Vertical swell, as a fraction of the element's height. */
  amplitude?: number;
  /** Multiplier on the drift speed. */
  speed?: number;
  /** Offsets the noise field. Give sibling surfaces different seeds so they
   *  don't all render the same wave. */
  seed?: number;
  className?: string;
}

/** Animated mesh-gradient backdrop. Absolutely positioned — give the parent
 *  `relative` (and `overflow-hidden` if it has rounded corners). */
export function GradientWave({
  colors,
  colorsLight,
  opacity = 1,
  amplitude = 0.55,
  speed = 1,
  seed = 0,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersLight = usePrefersLight();
  const prefersReducedMotion = useReducedMotion();

  // Read as a string so a fresh array literal from the caller can't restart
  // the effect on every render.
  const colorKey = (
    prefersLight ? (colorsLight ?? colors ?? COLORS_LIGHT) : (colors ?? COLORS_DARK)
  ).join();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;display:block";
    container.appendChild(canvas);

    let renderer: GradientRenderer;
    try {
      renderer = new GradientRenderer(canvas, {
        colors: colorKey.split(",") as GradientWaveColors,
        alpha: opacity,
        amplitude,
        speed,
        seed,
      });
    } catch (error) {
      // No WebGL, or too many live contexts on the page — the surface simply
      // stays flat rather than taking the section down with it.
      console.error("GradientWave: falling back to a flat surface.", error);
      container.removeChild(canvas);
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let inView = true;
    let running = false;
    let time = 0;
    let last = 0;
    let sized = false;

    const frame = (now: number) => {
      // clamp the step so a backgrounded tab doesn't jump the noise field
      time += Math.min(now - last, 1000 / 15) * speed;
      last = now;
      renderer.render(time);
      if (!inView) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (running || prefersReducedMotion || !sized) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    };

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width < 1 || height < 1) return;
      renderer.resize(width, height, dpr);
      sized = true;
      // a still frame, so the surface is never blank between resizes
      renderer.render(time);
      start();
    });
    ro.observe(container);

    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) start();
    });
    io.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      renderer.dispose();
      if (container.contains(canvas)) container.removeChild(canvas);
    };
  }, [colorKey, opacity, amplitude, speed, seed, prefersReducedMotion]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 z-0 overflow-hidden", className)}
    />
  );
}
